import { yupResolver } from '@hookform/resolvers/yup'
import { DefaultService } from '@LoopKitchen/typescript-client/lib/clients/FrontendDataService'
import { AddToHomeScreen } from '@mui/icons-material'
import { alpha, Box } from '@mui/material'
import { captureException } from '@sentry/nextjs'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import OTPInput from 'react-otp-input'
import CustomTypo, { CustomModalTypo } from 'src/components/mui/StyledTypo'
import { useSnackData } from 'src/context/SnackContext'
import useTimer from 'src/utils/hooks/useTimer'
import * as yup from 'yup'
import { useFastCasualContext } from '../context/FastCasualContext'

export interface OtpVerificationDialogValues {
  otp: string
}

const validationSchema = yup.object({
  otp: yup
    .string()
    .matches(/^\d{4}$/, 'Invalid OTP')
    .required('OTP is required')
})

interface OtpVerificationFormProps {
  handleResubmit: (e?: React.BaseSyntheticEvent) => Promise<void>
  email: string
  closeDialog: () => void
}

const OtpVerificationForm = ({
  handleResubmit,
  email,
  closeDialog
}: OtpVerificationFormProps) => {
  const { timer, resetTimer } = useTimer(30)
  const { openError, openSuccess } = useSnackData()
  const { setVerified } = useFastCasualContext()

  const [loading, setLoading] = React.useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      otp: ''
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit'
  })

  async function onSubmit(values: OtpVerificationDialogValues) {
    setLoading(true)
    try {
      const { otp } = values

      const res = await DefaultService.verifyEmailOtpVerifyEmailOtpPost({
        email,
        otp
      })

      if (res.verified) {
        openSuccess('Email verified successfully')
        localStorage.setItem('fc100access', 'true')
        setVerified(true)
        closeDialog()
      } else {
        openError(res.message)
      }
    } catch (error) {
      captureException(error)
    }
    setLoading(false)
  }

  useEffect(() => {
    resetTimer()
  }, [])

  return (
    <Box pt={'30px'}>
      <CustomModalTypo
        ta={'left'}
        fs={'32px'}
        m={'0px 0'}
        lh={'132%'}
        fw={600}
        w={'100%'}>
        Enter OTP
      </CustomModalTypo>
      <CustomTypo
        color={alpha('#122937', 0.5)}
        sx={{
          fontSize: {
            xs: 16,
            sm: 16,
            md: 14
          },
          lineHeight: '132%',
          fontWeight: 400,
          fontFamily: 'Sora',
          mb: {
            xs: 2,
            md: 3
          },
          ml: 0.5,
          textAlign: {
            xs: 'left'
          }
        }}>
        You will receive an OTP on your email
      </CustomTypo>
      <Box display="flex" flexDirection="column" gap={2}>
        <Controller
          control={control}
          name="otp"
          render={({ field: { value, onChange } }) => {
            return (
              <Box>
                <Box
                  sx={{
                    '& input': {
                      p: 1,
                      fontSize: 18
                    }
                  }}>
                  <OTPInput
                    value={value}
                    onChange={onChange}
                    numInputs={4}
                    containerStyle={{
                      width: 200,
                      justifyContent: 'space-between'
                    }}
                    inputStyle={{
                      width: 40
                    }}
                    shouldAutoFocus
                    renderSeparator={
                      <span style={{ marginLeft: 8, marginRight: 8 }}>-</span>
                    }
                    renderInput={(props) => (
                      <input
                        {...props}
                        style={{
                          ...props.style,
                          border: '1px solid #E6E6E6'
                        }}
                      />
                    )}
                  />

                  {errors.otp && errors.otp.message && (
                    <CustomTypo
                      color={'#d32f2f'}
                      sx={{
                        fontSize: 12,
                        lineHeight: '132%',
                        fontWeight: 400,
                        fontFamily: 'Sora',
                        mt: '8px'
                      }}>
                      {errors.otp.message}
                    </CustomTypo>
                  )}

                  <div className="flex justify-between items-center gap-2 mt-[24px]">
                    <button
                      onClick={async () => {
                        await handleResubmit()
                        resetTimer()
                      }}
                      disabled={timer !== 0}
                      className={`py-4 w-full text-[14px] outline outline-offset-[1px] outline-[#186174] outline-[1px] text-[#186174] text-center disabled:opacity-50 disabled:cursor-not-allowed rounded-[4px]`}>
                      Resend OTP {timer !== 0 && `(${timer}s)`}
                    </button>

                    <button
                      onClick={handleSubmit(onSubmit)}
                      disabled={loading}
                      className={`py-4 w-full rounded-[4px] text-[14px] bg-[#186174] text-white text-center disabled:opacity-50 disabled:cursor-not-allowed `}>
                      <AddToHomeScreen />
                      <span className="text-white ml-2">
                        Verify one time code from support@tryloop.ai in your
                        email inbox
                      </span>
                    </button>
                  </div>
                </Box>
              </Box>
            )
          }}
        />
      </Box>
    </Box>
  )
}

export default OtpVerificationForm
